'use client';

import React from 'react';
import styled from 'styled-components';
import { SectionHeader } from '@liftango/ui-elements';
import { IconButton } from '@liftango/ui-elements';
import { Icon } from '@liftango/ui-icons';
import { NavItem } from '@liftango/ui-elements';

interface SideNavContextValue {
  collapsed: boolean;
}

const SideNavContext = React.createContext<SideNavContextValue>({ collapsed: false });

/** Read the surrounding SideNavigation collapsed state. */
export function useSideNavCollapsed(): boolean {
  return React.useContext(SideNavContext).collapsed;
}

export interface SideNavigationProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Controlled collapsed state. The composite is purely controlled. */
  collapsed: boolean;
  children?: React.ReactNode;
}

export interface SideNavigationHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Brand mark / logo / wordmark; rendered next to the toggle when expanded. */
  brand?: React.ReactNode;
  /** Toggle handler. Hamburger button only renders when this is provided. */
  onToggle?: () => void;
  /** When provided, replaces the built-in chrome entirely. */
  children?: React.ReactNode;
}

export interface SideNavigationSectionProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Section label rendered as `SectionHeader` (uppercase, muted). Hidden when collapsed. */
  label: string;
  children?: React.ReactNode;
}

export interface SideNavigationFooterProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode;
}

export interface SideNavigationPinnedProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Replacement icon shown in collapsed mode (centred, icon-button-sized). */
  collapsedIcon: React.ReactNode;
  children?: React.ReactNode;
}

const Root = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  min-height: 0;
  overflow: hidden;
  background: ${(p) => p.theme.colors.neutral.panel};
  color: ${(p) => p.theme.colors.neutral.ink};
  font-family: ${(p) => p.theme.fontFamily.ui};
`;

const HeaderRow = styled.div<{ $collapsed: boolean }>`
  height: 52px;
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: ${(p) => p.theme.spaceAlias.sm};
  padding: ${(p) => (p.$collapsed ? '0' : `0 ${p.theme.spaceAlias.md}`)};
  justify-content: ${(p) => (p.$collapsed ? 'center' : 'flex-start')};
  border-bottom: 1px solid ${(p) => p.theme.colors.neutral.line};
  box-sizing: border-box;
`;

const BrandWrap = styled.div`
  display: flex;
  align-items: center;
  gap: ${(p) => p.theme.spaceAlias.sm};
  font-weight: ${(p) => p.theme.fontWeight.bold};
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const Body = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  padding: ${(p) => p.theme.spaceAlias.sm} 0;
`;

const SectionRoot = styled.div<{ $collapsed: boolean }>`
  display: flex;
  flex-direction: column;
  padding: ${(p) => (p.$collapsed ? `${p.theme.spaceAlias.xs} 0` : `${p.theme.spaceAlias.sm} 0`)};

  & + & {
    border-top: ${(p) => (p.$collapsed ? `1px solid ${p.theme.colors.neutral.line}` : 'none')};
  }
`;

const SectionLabelWrap = styled.div`
  padding: ${(p) => `${p.theme.spaceAlias.xs} ${p.theme.spaceAlias.md}`};
`;

const FooterRoot = styled.div`
  margin-top: auto;
  display: flex;
  flex-direction: column;
  flex: 0 0 auto;
  padding: ${(p) => p.theme.spaceAlias.sm} 0 0;
  border-top: 1px solid ${(p) => p.theme.colors.neutral.line};
  gap: ${(p) => p.theme.spaceAlias.xs};
`;

const PinnedRoot = styled.div<{ $collapsed: boolean }>`
  margin: ${(p) => p.theme.spaceAlias.sm};
  margin-top: ${(p) => p.theme.spaceAlias.sm};
  padding: ${(p) => (p.$collapsed ? p.theme.spaceAlias.xs : '0')};
  display: flex;
  align-items: center;
  justify-content: center;
  border-top: 1px solid ${(p) => p.theme.colors.neutral.line};
  padding-top: ${(p) => p.theme.spaceAlias.sm};
`;

/**
 * Walk a child list and inject `collapsed` into any direct `NavItem` child.
 * Other children pass through untouched.
 */
function injectCollapsedToNavItems(children: React.ReactNode, collapsed: boolean): React.ReactNode {
  return React.Children.map(children, (child) => {
    if (!React.isValidElement(child)) return child;
    if (child.type === NavItem) {
      return React.cloneElement(child as React.ReactElement<{ collapsed?: boolean }>, {
        collapsed,
      });
    }
    return child;
  });
}

/**
 * Sidebar navigation composite that fills `AppShell.SideNav`. Compose with
 * `SideNavigation.Header`, `SideNavigation.Section`, `SideNavigation.Footer`,
 * and `SideNavigation.Pinned`. The composite is purely controlled — the
 * caller owns `collapsed` and any `onToggle` wiring.
 *
 * Width is owned by the surrounding `AppShell.SideNav`; this composite only
 * reads `collapsed` and lays itself out vertically inside whatever width it
 * receives.
 */
export function SideNavigation({ collapsed, children, ...rest }: SideNavigationProps) {
  const ctx = React.useMemo(() => ({ collapsed }), [collapsed]);
  return (
    <SideNavContext.Provider value={ctx}>
      <Root {...rest}>{children}</Root>
    </SideNavContext.Provider>
  );
}

/**
 * Header row at the top of the side navigation. Renders an `IconButton`
 * hamburger (when `onToggle` is provided) followed by `brand`. If
 * `children` are passed, they replace the built-in chrome entirely.
 */
function Header({ brand, onToggle, children, ...rest }: SideNavigationHeaderProps) {
  const { collapsed } = React.useContext(SideNavContext);
  return (
    <HeaderRow $collapsed={collapsed} {...rest}>
      {children ?? (
        <>
          {onToggle && (
            <IconButton
              icon={<Icon name="list" />}
              label="Toggle navigation"
              variant="ghost"
              onClick={onToggle}
            />
          )}
          {!collapsed && brand && <BrandWrap>{brand}</BrandWrap>}
        </>
      )}
    </HeaderRow>
  );
}

/**
 * A labelled group of nav items. Renders the label via `SectionHeader` when
 * expanded; in collapsed mode, the label is hidden and adjacent sections
 * gain a 1px separator. NavItem children automatically inherit the current
 * collapsed state.
 */
function Section({ label, children, ...rest }: SideNavigationSectionProps) {
  const { collapsed } = React.useContext(SideNavContext);
  return (
    <SectionRoot $collapsed={collapsed} {...rest}>
      {!collapsed && (
        <SectionLabelWrap>
          <SectionHeader label={label} />
        </SectionLabelWrap>
      )}
      {injectCollapsedToNavItems(children, collapsed)}
    </SectionRoot>
  );
}

/**
 * Footer region pinned to the bottom of the side navigation. Sits beneath
 * the scrolling section area with a top hairline. NavItem children inherit
 * the current collapsed state.
 */
function Footer({ children, ...rest }: SideNavigationFooterProps) {
  const { collapsed } = React.useContext(SideNavContext);
  return <FooterRoot {...rest}>{injectCollapsedToNavItems(children, collapsed)}</FooterRoot>;
}

/**
 * Pinned slot — typically a small card at the very bottom of the sidebar.
 * Shows `children` when expanded, `collapsedIcon` when collapsed.
 */
function Pinned({ collapsedIcon, children, ...rest }: SideNavigationPinnedProps) {
  const { collapsed } = React.useContext(SideNavContext);
  return (
    <PinnedRoot $collapsed={collapsed} {...rest}>
      {collapsed ? collapsedIcon : children}
    </PinnedRoot>
  );
}

SideNavigation.Header = Header;
SideNavigation.Section = Section;
SideNavigation.Footer = Footer;
SideNavigation.Pinned = Pinned;
